import { BaseTimeSource } from './base-time-source';

export class PlaybackTimeSource extends BaseTimeSource {
    private playing = true;
    private currentTimeSeconds = 0;
    private currentTimeSetAt = performance.now();
    private pausedAt = performance.now();
    private targetTime: number | null = null;
    private speed: number = 1.0;
    private interval: number | null = null;
    private timeout: number | null = null;

    get currentTime(): number {
        return this.currentTimeSeconds;
    }

    get currentTimeMs(): number {
        if (this.targetTime && this.currentTime >= this.targetTime) {
            return this.currentTime * 1000;
        }

        if (this.playing) {
            return this.currentTimeSeconds * 1000 + Math.min(999, (performance.now() - this.currentTimeSetAt) * this.speed);
        }

        // Simulate milliseconds elapsed up until the timer was paused
        return this.currentTimeSeconds * 1000 + Math.min(999, (this.pausedAt - this.currentTimeSetAt) * this.speed);
    }

    setSpeed(speed: number) {
        this.speed = speed;
        if (this.playing) {
            this.restartLoop();
        }
    }

    setNow(nowSeconds: number, target?: number) {
        const previousTime = this.currentTimeSeconds;
        this.currentTimeSeconds = nowSeconds;
        this.currentTimeSetAt = performance.now();
        this.targetTime = target ?? null;

        if (this.playing) {
            this.restartLoop();
        } else {
            this.pausedAt = this.currentTimeSetAt;
        }

        if (previousTime !== this.currentTimeSeconds) {
            this.fire(this.currentTimeSeconds);
        }
    }

    resume() {
        if (this.playing) {
            return;
        }

        const elapsedBeforePause = this.pausedAt - this.currentTimeSetAt;
        this.currentTimeSetAt = performance.now() - elapsedBeforePause;
        this.playing = true;
        this.timeout = self.setTimeout(
            () => {
                this.timeout = null;
                this.update();
                this.restartLoop();
            },
            1000 / this.speed - (elapsedBeforePause % (1000 / this.speed))
        );
    }

    pause() {
        if (!this.playing) {
            return;
        }

        this.playing = false;
        this.pausedAt = performance.now();
        if (this.timeout !== null) {
            self.clearTimeout(this.timeout);
            this.timeout = null;
        }

        if (this.interval !== null) {
            self.clearInterval(this.interval);
            this.interval = null;
        }
    }

    private restartLoop() {
        if (this.timeout !== null) {
            self.clearTimeout(this.timeout);
            this.timeout = null;
        }

        if (this.interval !== null) {
            clearInterval(this.interval);
        }
        this.interval = self.setInterval(() => this.update(), 1000 / this.speed);
    }

    private update = () => {
        if (!this.targetTime) {
            return;
        }

        if (this.currentTimeSeconds >= this.targetTime) {
            return;
        }

        this.currentTimeSeconds++;
        this.currentTimeSetAt = performance.now();
        this.fire(this.currentTimeSeconds);
    };
}
